import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  Flashbar,
  FormField,
  Modal,
  SpaceBetween,
  Textarea,
} from '@amzn/awsui-components-react-v3/polaris';
import { Messages } from '../../Common/ErrorMessages';
// The base no-unused-vars rule does not see type-only uses.
// eslint-disable-next-line no-unused-vars
import { ApiError } from '../../../utils/serverErrors';
import * as changeSetReview from './changeSetReview';

interface ChangeSetReviewProps {
  apiClient: { httpPost: (path: string, body: object) => Promise<unknown> };
  // A pending change set, or a failed one when `failed` is set.
  changeSetId: string;
  // Its approval failed part way; it can only be approved again, which applies the rest.
  failed?: boolean;
  // What the approve dialog says approving does.
  approveDescription?: string;
  // Called when the change set is reviewed or turns out to be no longer pending.
  onReviewed: (outcome: changeSetReview.ReviewOutcome) => void;
}

const DEFAULT_APPROVE_DESCRIPTION =
  'Approving makes this change set the live version of the model.';

const ACTIONS: Record<
  changeSetReview.ReviewAction,
  { label: string; done: string; header: (changeSetId: string) => string }
> = {
  approve: {
    label: 'Approve',
    done: 'Approved',
    header: (changeSetId): string => `Approve change set ${changeSetId}?`,
  },
  reject: {
    label: 'Reject',
    done: 'Rejected',
    header: (changeSetId): string => `Reject change set ${changeSetId}?`,
  },
};

/**
 * Approve and Reject buttons for a pending change set, or only "Retry approval" for a failed one.
 * Each opens a dialog that sends the review only once confirmed; a reject may carry a comment.
 * Used by the pricing and scoping model pages and the change set page.
 */
const ChangeSetReview = ({
  apiClient,
  changeSetId,
  failed = false,
  approveDescription = DEFAULT_APPROVE_DESCRIPTION,
  onReviewed,
}: ChangeSetReviewProps): JSX.Element => {
  const approveLabel = failed ? 'Retry approval' : ACTIONS.approve.label;
  const [action, setAction] = useState<changeSetReview.ReviewAction | null>(null);
  const [comment, setComment] = useState('');
  const [commentError, setCommentError] = useState<string | undefined>(undefined);
  const [errors, setErrors] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const open = (next: changeSetReview.ReviewAction): void => {
    setAction(next);
    setComment('');
    setCommentError(undefined);
    setErrors([]);
  };

  const close = (): void => {
    if (!submitting) {
      setAction(null);
    }
  };

  const submit = (reviewing: changeSetReview.ReviewAction): void => {
    const validationError =
      reviewing === 'reject' ? changeSetReview.validateComment(comment) : undefined;
    setCommentError(validationError);
    if (validationError) {
      return;
    }
    setErrors([]);
    setSubmitting(true);
    const body = reviewing === 'reject' ? changeSetReview.toRejectRequest(comment) : {};
    apiClient
      .httpPost(changeSetReview.changeSetReviewPath(changeSetId, reviewing), body)
      .then(() => {
        setSubmitting(false);
        setAction(null);
        onReviewed({
          type: 'success',
          message: `${ACTIONS[reviewing].done} change set ${changeSetId}`,
        });
      })
      .catch((error: ApiError) => {
        setSubmitting(false);
        if (changeSetReview.isUnreviewable(error)) {
          setAction(null);
          onReviewed({ type: 'error', message: changeSetReview.describeUnreviewable(error) });
          return;
        }
        setErrors(changeSetReview.describeError(error));
      });
  };

  return (
    <>
      <SpaceBetween direction="horizontal" size="xs">
        <Button id="approveChangeSet" variant="primary" onClick={(): void => open('approve')}>
          {approveLabel}
        </Button>
        {!failed && (
          <Button id="rejectChangeSet" onClick={(): void => open('reject')}>
            Reject
          </Button>
        )}
      </SpaceBetween>
      {action && (
        <Modal
          id={`${action}ChangeSetModal`}
          visible
          closeAriaLabel="Close"
          onDismiss={close}
          header={ACTIONS[action].header(changeSetId)}
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button
                  id="cancelChangeSetReview"
                  variant="link"
                  disabled={submitting}
                  onClick={close}
                >
                  Cancel
                </Button>
                <Button
                  id="confirmChangeSetReview"
                  variant="primary"
                  loading={submitting}
                  onClick={(): void => submit(action)}
                >
                  {action === 'approve' ? approveLabel : ACTIONS[action].label}
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          <SpaceBetween size="m">
            {errors.length > 0 && (
              <Alert id="changeSetReviewErrors" type="error">
                <Messages messages={errors} />
              </Alert>
            )}
            {action === 'approve' ? (
              <Box>{approveDescription}</Box>
            ) : (
              <FormField
                id="reviewComment-field"
                label="Comment"
                description="Optional"
                constraintText={`${changeSetReview.commentLength(comment)}/${
                  changeSetReview.MAX_COMMENT_LENGTH
                } characters`}
                errorText={commentError}
              >
                <Textarea
                  id="reviewComment"
                  value={comment}
                  invalid={Boolean(commentError)}
                  disabled={submitting}
                  onChange={({ detail }): void => {
                    setComment(detail.value);
                    setCommentError(undefined);
                  }}
                />
              </FormField>
            )}
          </SpaceBetween>
        </Modal>
      )}
    </>
  );
};

ChangeSetReview.propTypes = {
  apiClient: PropTypes.object.isRequired,
  changeSetId: PropTypes.string.isRequired,
  failed: PropTypes.bool,
  approveDescription: PropTypes.string,
  onReviewed: PropTypes.func.isRequired,
};

/** The outcome of the last review on a model page, until dismissed. */
export const ReviewOutcomeFlash = ({
  outcome,
  onDismiss,
}: {
  outcome: changeSetReview.ReviewOutcome | null;
  onDismiss: () => void;
}): JSX.Element | null =>
  outcome && (
    <Flashbar
      id="changeSetReviewOutcome"
      items={[{ type: outcome.type, content: outcome.message, dismissible: true, onDismiss }]}
    />
  );

export default ChangeSetReview;
